import { type TimeStateMachine, TimeStates } from "@/models/time-control/timeStateTypes";
import type { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import { useSelector } from "@xstate/react";
import type { DateTime, Duration } from "luxon";
import { useContext } from "react";
import type { StateFrom } from "xstate";
import { TimeStateMachineContext } from "./timeStateContext";

/**
 * Returns a running instance of time state machine (service)
 *
 * Note that this will NOT rerender upon state change.
 */

export function useTimeService() {
  const { timeStateService } = useContext(TimeStateMachineContext);
  return {
    timeService: timeStateService,
    send: timeStateService.send,
  };
}

export function useCurrTimeState(): StateFrom<TimeStateMachine> {
  const { timeService } = useTimeService();
  return useSelector(timeService, (state) => state);
}

export function useDisplayTime(): DateTime {
  const { timeService } = useTimeService();
  const displayTime = useSelector(timeService, (snapshot) => snapshot.context.displayTime);
  return displayTime;
}
export function useNextDisplayTime(): DateTime {
  const { timeService } = useTimeService();
  const nextDisplayTime_ = useSelector(timeService, (snapshot) => snapshot.context.nextDisplayTime_);
  return nextDisplayTime_;
}

export function useTimezone(): GuiTimeZone {
  const { timeService } = useTimeService();
  const timezone = useSelector(timeService, (snapshot) => snapshot.context.timezone);
  return timezone;
}

export function useAnimationFps() {
  const state = useCurrTimeState();
  return state.matches(TimeStates.ANIMATION) ? state.context.modeOpts?.fps : null;
}

export function useIsLocked() {
  const state = useCurrTimeState();
  return state.matches(TimeStates.AUTO_REFRESH) ? state.context.modeOpts?.isLocked : null;
}

export function useDefaultRange(defaultDayOffset = 7, startTime?: DateTime): [DateTime, DateTime] {
  const state = useCurrTimeState();
  const displayTime = state.context.displayTime;

  return [
    startTime ? startTime : displayTime,
    (startTime ? startTime : displayTime).plus({ days: defaultDayOffset }),
  ] as const;
}
